import React, { useEffect, useState, useMemo, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { formatValueAsDate } from "../../../../../helpers/utils/DateUtils";
import { DataTable } from "../../../../../components/common/datatable/DataTable";
import Form from "../../../../../components/common/form/Form";
import CommonChosenInput from "../../Sections/CommonChosenInput";
import { createOptionTypeListFromArray, loadPdf, selectDocument, showToast } from "../../../../../helpers/utils/Utils";
import Modal from "../../../../../components/common/form/Modal";
import { DataTableColumn } from "../../../../../components/common/datatable/DataTableTypes";
import {
    useDeliverPdfMutation,
    useGetOrderSelectedFilesQuery, useLazyGetContactEmailQuery, useMarkedProductFilesMutation,
    useSearchAllFileDataNewQuery,
    useSearchAllFileDataOldQuery, useUnMarkedProductFilesMutation
} from "../../../ordersApi";
import { useNavigate } from "react-router-dom";
import { tgxApi } from "../../../../../app/tgxApi";
import { hasPermission } from "../../../../../helpers/utils/AccessControlUtils";
import Button from "../../../../../components/common/form/Button";
import MultiFreeFormField from "../../../../../components/common/form/MultiFreeFormField";


interface Document {
    id: string;
    filename: string;
    fileType: string;
    dateDelivered?: string;
}


interface PdfDeliveryConfirmationModalProps {
    title: string;
    body: string;
    docList: any;
    deliveryEmail: any;
    setDeliveryEmail: any
    show: boolean;
    setShow: any;
    disable: boolean;
    setDisable: any;
    orderId: any;
    productType: string;
    isSendNotification: boolean;
    fileType: string;
    isHoaOrder: boolean;
    hoaCount?: any;
    orderData: any;
    includeCadPrintout?: boolean;
    setDocList: any;
}

const PdfDeliveryConfirmationModal: React.FC<PdfDeliveryConfirmationModalProps> = ({
    title,
    body,
    docList,
    deliveryEmail,
    setDeliveryEmail,
    show,
    setShow,
    disable,
    setDisable,
    orderId,
    productType,
    isSendNotification,
    fileType,
    isHoaOrder,
    hoaCount = null,
    orderData = null,
    includeCadPrintout = false,
    setDocList,
}) => {

    const navigate = useNavigate();
    const [props, setProps] = useState<string | null>(null);
    const [newData, setNewData] = useState<Document[]>([]);
    const [oldData, setOldData] = useState<Document[]>([]);
    const [otherSelectedDoc, setOtherSelectedDoc] = useState<Document[]>([]);
    const [isPackagedDelivery, setIsPackagedDelivery] = useState(
        orderData.data && orderData.data.branchDTO.packagedDelivery
    );
    const [date, setDate] = useState("");
    const [productId, setProductId] = useState(
        orderData.data &&
        orderData.data?.products.filter((el: any) => el.type.includes(productType))[0].id
    );

    const [enableMarkedBtn, setEnableMarkedBtn] = useState(false);
    const [contactEmailsDataApi, contactEmailsData] = useLazyGetContactEmailQuery();

    const newDocumentList = useSelector((state: any) => state.getAllDocumentReducerNew);
    const otherDocumentList = useSelector((state: any) => state.getAllDocumentReducerOld);

    const dispatch = useDispatch();
    const [deliverPDF, deliverPDFResponse] = useDeliverPdfMutation();
    const formType = productType === "TAX_CERTIFICATE" ? "tax_cert" : "Hoa";
    const shouldFetch = !!orderId && !!productType; // skip if either is missing
    const [unMarkedProductFiles, unMarkedProductFilesResponse] = useUnMarkedProductFilesMutation();
    const [markedProductFiles] = useMarkedProductFilesMutation();

    const { data: selectedFiles } = useGetOrderSelectedFilesQuery(
        {
            orderId: orderId,
            formType: formType,
        },
        {
            skip: !shouldFetch,
        }
    );

    const [markedFilesList, setMarkedFilesList] = useState<Document[]>(
        orderData?.data?.branchDTO?.packagedDelivery
            ? (orderData.data?.products
                ?.find((el: any) => el.type.includes(productType))
                ?.markedFiles || []
            ).map((id: string) => ({ id }))
            : []
    );

    const [enableUnMarkedBtn, setEnableUnMarkedBtn] = useState(
        markedFilesList.length > 0
    );

    useEffect(() => {
        dispatch(tgxApi.util.invalidateTags([
            "getContactEmailByOrderId",
        ]));
    }, []);

    useEffect(() => {
        if (!selectedFiles) {
            return;
        }
        let list = [...selectedFiles];
        setDocList(list);
        if (list.length > 0) {
            setDisable(false)
        }
        loadPdf(selectedFiles[0].id);

        if (isPackagedDelivery && markedFilesList.length === 0) {
            setMarkedFilesList(list);
            setEnableMarkedBtn(true);
        }

    }, [selectedFiles]);

    let customFilterForNew = {
        sortOrder: "desc",
        orderId: orderId,
        orderFileType: includeCadPrintout ? [fileType, "CADPrintout"] : [fileType],
        type: "New",
    };

    let customFilterForOld = {
        ...customFilterForNew,
        orderFileType: isHoaOrder
            ? [fileType, "SOA", "SOA_REQUEST"]
            : includeCadPrintout
                ? [fileType, "CADPrintout"]
                : [fileType],
        type: "Old",
    };

    function dismissModal(e: React.MouseEvent) {
        e.preventDefault();
        setShow(false);
        setDeliveryEmail([]);
        dispatch(tgxApi.util.invalidateTags([
            "getContactEmailByOrderId",
        ]));
    }

    const computeNewFileDelivery = (
        documentList: Document[],
        setDocumentList?: (docs: Document[]) => void
    ) => {
        loadPdf(newDocumentList.items?.data[0].id);
        documentList.length === 0 &&
            newDocumentList.items?.data.map((cur: any) => {
                setEnableMarkedBtn(true);

                if (!(Array.isArray(markedFilesList) && markedFilesList.length > 0)) {
                    return;
                }

                if (
                    markedFilesList.includes(cur.id) &&
                    documentList.filter((cur1) => cur1.id === cur.id).length === 0
                ) {
                    documentList.push(cur);
                }

                setDate("render" + Date.now());
            });

        if (setDocumentList) {
            let list = [...documentList];
            setDocumentList(list);
        }

        setDate("render" + Date.now());
    };

    const computeOldFileDelivery = (
        documentList: Document[],
        setDocumentList?: (docs: Document[]) => void
    ) => {
        otherDocumentList.items.data &&
            otherDocumentList.items.data.map((cur: any) => {
                if (
                    Array.isArray(markedFilesList) &&
                    markedFilesList.length > 0 &&
                    documentList.filter((cur1) => cur1.id === cur.id).length === 0
                ) {
                    setEnableUnMarkedBtn(true);
                    if (markedFilesList.includes(cur.id)) {
                        documentList.push(cur);
                    }
                } else {
                    setEnableMarkedBtn(true);
                }
            });

        setDocumentList && setDocumentList(documentList);
    };

    useEffect(() => {
        if (
            newDocumentList?.data &&
            newDocumentList?.data?.data != null &&
            newDocumentList?.data?.data.length > 0
        ) {
            setProps(newDocumentList?.data[0].id);
            setNewData(newDocumentList?.data);
        }

        if (
            newDocumentList?.data &&
            otherDocumentList?.data &&
            selectedFiles?.data
        ) {
            if (!isPackagedDelivery) {
                computeOldFileDelivery(otherSelectedDoc);
                computeNewFileDelivery(docList);
            } else {
                if (markedFilesList?.length === 0 && docList?.length > 0) {
                    setEnableMarkedBtn(true);
                }
                computeNewFileDelivery(
                    markedFilesList?.length > 0 ? markedFilesList : docList,
                    setMarkedFilesList
                );
                computeOldFileDelivery(
                    markedFilesList?.length > 0 ? markedFilesList : docList,
                    setMarkedFilesList
                );
            }
        }
    }, [newDocumentList, otherDocumentList, docList]);

    useEffect(() => {
        if (orderData.data) {
            contactEmailsDataApi(orderData.data);
        }
    }, [orderData.data]);

    useEffect(() => {
        if (contactEmailsData?.data && contactEmailsData?.data.length > 0) {
            setDeliveryEmail(contactEmailsData?.data ?? []);
        }
    }, [contactEmailsData]);


    useEffect(() => {
        if (!props) {
            return;
        }

        [...newData, ...oldData].forEach((curData) => {
            const el = document.getElementById(curData.id);
            if (el) {
                const tr = el.closest("tr");
                if (tr) {
                    tr.classList.remove(
                        productType.includes("TAX_") ? "bg-blue-300" : "bg-green-400"
                    );
                }
            }
        });

        const element = document.getElementById(props);
        if (element) {
            const tr = element.closest("tr");
            if (tr) {
                tr.classList.add(
                    productType.includes("TAX_") ? "bg-blue-300" : "bg-green-400"
                );
            }
        }
    }, [props, newData, oldData, productType]);

    const handleCheckboxChange = (
        row: { original: Document },
        list: Document[],
        setList: (docs: Document[]) => void
    ) => {
        const newList = list.map((e) => e.id).includes(row.original.id)
            ? list.filter((item) => item.id !== row.original.id)
            : [...list, row.original];

        setList(newList);
        setEnableMarkedBtn(newList.length > 0);
        setEnableUnMarkedBtn(false);
        setDate(row.original.id + Date.now());
        setProps(row.original.id);
        loadPdf(row.original.id);
    };

    const checkboxColumn = (
        row: { original: Document },
        list: Document[],
        setList: (docs: Document[]) => void
    ) => {
        const isChecked = list.map((e) => e.id).indexOf(row.original.id) > -1;
        return (
            <input
                type="checkbox"
                id={row.original.id}
                onChange={() => handleCheckboxChange(row, list, setList)}
                onClick={() => {
                    setProps(row.original.id);
                    loadPdf(row.original.id);
                    setEnableMarkedBtn(true);
                    setEnableUnMarkedBtn(false);
                }}
                defaultChecked={isChecked}
            />
        );
    };

    const columns = (list: any, setList: any): DataTableColumn[] => [
        {
            label: "",
            accessor: "id",
            canSort: false,
            cell: (props: any) =>
                checkboxColumn(props.row, list, setList),
        },
        {
            label: "Name",
            accessor: "filename",
            cell: (props: any) =>
                selectDocument(
                    props,
                    list === docList ? setNewData : setOldData,
                    setProps
                ),
        },
        {
            label: "Type",
            accessor: "fileType",
            cell: (props: any) =>
                selectDocument(
                    props,
                    list === docList ? setNewData : setOldData,
                    setProps
                ),
        },
        {
            label: "Delivered",
            accessor: "dateDelivered",
            cell: (props: any) =>
                props.row.original.dateDelivered
                    ? formatValueAsDate(props.row.original.dateDelivered)
                    : "-",
        },
    ];

    const columnsForNew = useMemo(
        () =>
            columns(
                !isPackagedDelivery ? docList : markedFilesList,
                !isPackagedDelivery ? setDocList : setMarkedFilesList
            ),
        [date, docList, markedFilesList]
    );

    const columnsForOld = useMemo(
        () =>
            columns(
                !isPackagedDelivery ? otherSelectedDoc : markedFilesList,
                !isPackagedDelivery ? setOtherSelectedDoc : setMarkedFilesList
            ),
        [date, docList, markedFilesList]
    );

    const deliveryAction = async () => {
        if (
            (docList.length > 0 || otherSelectedDoc.length > 0) &&
            (!isSendNotification || deliveryEmail.length > 0)
        ) {
            setDisable(true);

            const combinedDocs = [...docList, ...otherSelectedDoc];

            try {
                const result = await deliverPDF({
                    orderId,
                    deliveryEmail: deliveryEmail.map((email: any) => email),
                    productId,
                    docList: combinedDocs,
                }).unwrap();

                if (result?.errorCode === 200) {
                    dispatch(tgxApi.util.invalidateTags([
                        "getContactEmailByOrderId",
                        "getAllFileDataNew",
                        "getAllFileDataOld",
                        "orderDocumentList",
                        "orderLogs"
                    ]));

                    navigate("/orders/" + orderId);
                } else {

                    showToast(result?.message, "error");
                    // Trigger refetch of file data only (via invalidation)
                    dispatch(tgxApi.util.invalidateTags([
                        "getAllFileDataNew",
                        "getAllFileDataOld"
                    ]));
                }
                if (hasPermission(["FILE-R"])) {
                    dispatch(tgxApi.util.invalidateTags(["orderDocumentList"]));
                }

                if (hasPermission(["OLOG-R"])) {
                    dispatch(tgxApi.util.invalidateTags(["orderLogs"]));
                }

                setShow(false);
                setDisable(false);
            } catch (error: any) {
                showToast(error?.message || "Error delivering PDF", "error");

                dispatch(tgxApi.util.invalidateTags([
                    "getAllFileDataNew",
                    "getAllFileDataOld"
                ]));

                if (hasPermission(["FILE-R"])) {
                    dispatch(tgxApi.util.invalidateTags(["orderDocumentList"]));
                }

                if (hasPermission(["OLOG-R"])) {
                    dispatch(tgxApi.util.invalidateTags(["orderLogs"]));
                }

                setDisable(false);
            }
        }
    };

    const markedAction = async () => {
        if (markedFilesList.length > 0 && enableMarkedBtn) {
            setDisable(true);   
            try {
                const result = await markedProductFiles({
                    productId,
                    files: markedFilesList,
                }).unwrap();
                if (result?.errorCode === 200) {
                    setDisable(false);
                    setShow(false);
                    navigate("/orders/" + orderId);
                }
            } catch (error) {
                setDisable(false);
            }
        }
    };

    const unMarkedProductFileAction = async () => {
        if (markedFilesList.length > 0 && enableUnMarkedBtn) {
            // setDisableSecond(true);
            try {
                await unMarkedProductFiles(productId).unwrap();
            } catch (error) {
                // setDisableSecond(false);
            }
        }
    };

    const getButtonLabel = (orderData: any, loading: boolean) => {
        return !loading
            ? orderData.data && orderData.data.branchDTO.packagedDelivery
                ? "Mark for Delivery"
                : "Deliver"
            : orderData.data && orderData.data.branchDTO.packagedDelivery
                ? "Mark for Delivery..."
                : "Deliver...";
    };

    const getButtonStyle = () => {
        if (isHoaOrder) {
            const isNewDeliveryValid =
                (docList.length > 0 || otherSelectedDoc.length > 0) &&
                !isPackagedDelivery;
            const isPackagedDeliveryValid =
                isPackagedDelivery &&
                (markedFilesList.length > 0 || otherSelectedDoc.length > 0) &&
                enableMarkedBtn &&
                !enableUnMarkedBtn;

            return isNewDeliveryValid || isPackagedDeliveryValid
                ? productType.includes("TAX_")
                    ? "px-[14px] py-[5px] bg-highlight-5 text-text-0 border-transparent rounded-[11px] m-[7px_4px] shadow-sm"
                    : "px-[14px] py-[5px] bg-text-4 text-text-0 border-transparent rounded-[11px] m-[7px_4px] shadow-sm"
                : "px-[14px] py-[5px] bg-[#969da4] text-text-0 border-transparent rounded-[11px] m-[7px_4px] shadow-sm";

        } else {
            const hasTaxCertificate = docList.some(
                (el: any) => el.fileType === "TaxCertificate"
            );
            const hasMarkedTaxCertificate = markedFilesList.some(
                (el: any) => el.fileType === "TaxCertificate"
            );
            const hasOtherTaxCertificate = otherSelectedDoc.some(
                (el: any) => el.fileType === "TaxCertificate"
            );

            const isNewDeliveryValid =
                !isPackagedDelivery &&
                (docList.length > 0 || otherSelectedDoc.length > 0) &&
                (hasTaxCertificate || hasOtherTaxCertificate);
            const isPackagedDeliveryValid =
                isPackagedDelivery &&
                (markedFilesList.length > 0 || otherSelectedDoc.length > 0) &&
                enableMarkedBtn &&
                !enableUnMarkedBtn &&
                (hasMarkedTaxCertificate || hasOtherTaxCertificate);

            return (isNewDeliveryValid || isPackagedDeliveryValid)
                ? productType.includes("TAX_")
                    ? "px-[14px] py-[5px] bg-highlight-5 text-text-0 border-transparent rounded-[11px] ml-0 my-[7px] mx-[4px] shadow-sm"
                    : "px-[14px] py-[5px] bg-text-4 text-text-0 border-transparent rounded-[11px] ml-0 my-[7px] mx-[4px] shadow-sm"
                : "px-[14px] py-[5px] bg-[#969da4] text-text-0 border-transparent rounded-[11px] ml-0 my-[7px] mx-[4px] shadow-sm";

        }
    };

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const hasTaxCertificate = docList.some(
            (el: any) => el.fileType === "TaxCertificate"
        );
        const hasMarkedTaxCertificate = markedFilesList.some(
            (el: any) => el.fileType === "TaxCertificate"
        );
        const hasOtherTaxCertificate = otherSelectedDoc.some(
            (el: any) => el.fileType === "TaxCertificate"
        );

        if (
            !isPackagedDelivery &&
            ((isHoaOrder && (docList.length > 0 || otherSelectedDoc.length > 0)) ||
                (!isHoaOrder &&
                    (docList.length > 0 || otherSelectedDoc.length > 0) &&
                    (hasTaxCertificate || hasOtherTaxCertificate)))
        ) {
            deliveryAction();
        } else if (
            isPackagedDelivery &&
            ((isHoaOrder &&
                (markedFilesList.length > 0 || otherSelectedDoc.length > 0)) ||
                (!isHoaOrder &&
                    (markedFilesList.length > 0 || otherSelectedDoc.length > 0) &&
                    (hasMarkedTaxCertificate || hasOtherTaxCertificate)))
        ) {
            markedAction();
        }
    }

    const handleDeliveryEmailsChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: any }) => {
        const { value } = "target" in e ? e.target : e;
        setDeliveryEmail(value);
    };

    return (
        <>
            <Modal
                title={title}
                show={show}
                setShow={setShow}
                className={"max-w-[85%]"}
                hoa={isHoaOrder}
                fields={
                    <>
                        <div
                            className="modal-body max-h-[calc(100vh-100px)] overflow-auto [-ms-overflow-style:none] [scrollbar-width:none]">
                            <p className="ml-1 text-sm">{body}</p>

                            <div className={"grid grid-cols-12 gap-4 "}>
                                <div className={"col-span-6"}>
                                    <h2 className="text-3xl font-medium text-surface-2">New Documents</h2>
                                    <div
                                        className={
                                            productType.includes("TAX_")
                                                ? "table_data"
                                                : "table_data hoa_table"
                                        }
                                    >
                                        <div>
                                            <DataTable
                                                columns={columnsForNew}
                                                actions={{
                                                    fetchAction: useSearchAllFileDataNewQuery,
                                                }}
                                                defaultFilter={{
                                                    pageNumber: 0,
                                                    pageSize: 5,
                                                    ...customFilterForNew
                                                }}
                                                renderOptions={{
                                                    skipUserSettingRender: true,
                                                    skipFilterButtonRender: true
                                                }}
                                                appendFilterToUrl={false}
                                                isHoa={isHoaOrder}
                                                classname={isHoaOrder ? "bg-highlight-1 text-text-0" : "bg-highlight-0 text-text-0"}
                                            />
                                            {productType.includes("TAX_") &&
                                                hoaCount !== null &&
                                                docList.length !== 0 &&
                                                docList.length < hoaCount && (
                                                    <p className={"mt-2 error_cl"}>
                                                        You have selected {docList.length} files expected file
                                                        count to be delivered is {hoaCount}.
                                                    </p>
                                                )}
                                        </div>
                                    </div>

                                    <h2 className="text-3xl font-medium text-surface-2">Other Documents</h2>
                                    <div
                                        className={
                                            productType.includes("TAX_")
                                                ? "table_data"
                                                : "table_data hoa_table"
                                        }
                                    >
                                        <div className="table-responsive jurisdictions_selector">
                                            <DataTable

                                                columns={columnsForOld}
                                                actions={{
                                                    fetchAction: useSearchAllFileDataOldQuery,
                                                }}
                                                defaultFilter={{
                                                    pageNumber: 0,
                                                    pageSize: 5,
                                                    ...customFilterForOld
                                                }}
                                                renderOptions={{
                                                    skipUserSettingRender: true,
                                                    skipFilterButtonRender: true
                                                }}
                                                appendFilterToUrl={false}
                                                isHoa={isHoaOrder}
                                                classname={isHoaOrder ? "bg-highlight-1 text-text-0" : "bg-highlight-0 text-text-0"}

                                            />

                                            {productType.includes("TAX_") &&
                                                hoaCount !== null &&
                                                docList.length !== 0 &&
                                                docList.length < hoaCount && (
                                                    <p className={"mt-2 error_cl"}>
                                                        You have selected {docList.length} files expected file
                                                        count to be delivered is {hoaCount}.
                                                    </p>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                <div className={"col-span-6"}>
                                    <iframe
                                        src=""
                                        style={{
                                            width: "100%",
                                            minHeight: "500px",
                                            overflow: "visible",
                                        }}
                                        width="100%"
                                        height="100%"
                                        className="border border-surface-5"
                                    />
                                </div>
                            </div>
                        </div>
                        <Form
                            onSubmit={(e) => {
                                handleSubmit(e);
                            }}
                            fields={
                                <div className="flex content-between modal-footer border-t border-surface-5 mt-3 py-3">
                                    <div className="d-flex gap grow">
                                        {isSendNotification && !isPackagedDelivery && (
                                            <>
                                             
                                                <MultiFreeFormField
                                                    defaultOptions={createOptionTypeListFromArray(deliveryEmail)}
                                                    placeholder="abc@org.com..."
                                                    onChange={handleDeliveryEmailsChange}
                                                    name="deliveryEmails"
                                                    label="Notification"
                                                    required
                                                />
                                            </>
                                        )}
                                    </div>

                                    <div className={'flex gap-3 items-center'}>

                                        {markedFilesList.length > 0 && isPackagedDelivery &&
                                            <Button btnText={'Unmark for Delivery'}
                                                onClick={(e: any) => {
                                                    e.preventDefault();
                                                    unMarkedProductFileAction();
                                                    setShow(false);
                                                    setDisable(false);
                                                }} type={"button"}
                                                isLoading={unMarkedProductFilesResponse.isLoading}
                                                isHoa={isHoaOrder}
                                                isDisabled={!enableUnMarkedBtn}
                                            />
                                        }

                                        <Button btnText={'No'}
                                            onClick={(e: any) => {
                                                dismissModal(e);
                                            }} type={"close"}
                                        />


                                        {disable ? (
                                            <Button btnText={getButtonLabel(orderData, true)}
                                                type={"button"}
                                                isHoa={isHoaOrder}
                                                isDisabled={true}
                                            />

                                        ) : (
                                            <>
                                                <button className={getButtonStyle()}>
                                                    {getButtonLabel(orderData, false)}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            }
                        />
                    </>
                }
            />
        </>
    );
};

export default PdfDeliveryConfirmationModal;